// Core
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

// Libraries
import { Trans, useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import _ from 'lodash';
import { MenuItemType } from 'antd/lib/menu/hooks/useItems';

// Plugins
import { lastOperationalUnitDataVSI } from '../../../../../plugins/crusher';
import { timestampToTimeAgo } from '../../../../../plugins/general';
import { RequestMethod, requestVantageApi } from '../../../../../plugins/request';
import {
  convertEdgeStatus,
  extractValue,
} from '../../../../../plugins/trendings';

// Store
import {
  getCustomer,
  getUser,
} from '../../../../../store/session.store';

// Types
import {
  CrusherDashboardChartRange,
  CrusherOperationalData,
  EquipmentUnitDataVSICrusher,
  TrendingType,
} from '../../../../../types/crusher';
import { Theme } from '../../../../../types/theme';
import {
  Customer,
  EdgeDeviceOperationalStatus,
  EdgeDeviceOperationalStatusStatus,
  User,
  UserType,
} from '../../../../../types/global';

// Hooks
import { useBreakpoint } from '../../../../../hooks/useBreakpoint';

// Components
import { Button, ButtonSize, ButtonType } from '../../../../../layout/elements/Button';
import { Loader, LoaderSize } from '../../../../../layout/elements/Loader';
import { ModalPanel, ModalSize } from '../../../../../layout/elements/ModalPanel';
import { NavigationTabs } from '../../../../../layout/elements/NavigationTabs';
import { DatePicker, DatePickerProps } from '../../../../../layout/elements/DatePicker';
import { EmptyBox } from '../../../../../components/EmptyBox';
import { OptionsSwitch, OptionsSwitchProps } from '../../../../../layout/elements/OptionsSwitch';
import {
  Paragraph,
  ParagraphSize,
} from '../../../../../layout/elements/Paragraph';
import { showToast, ToastType } from '../../../../../layout/elements/Toast';
import { CloudOffLine, Plus } from '../../../../../layout/icons';
import { RotateDeviceOverlay } from '../../../../../components/RotateDeviceOverlay';

// Trendings Chart
import { CustomChart } from './CustomChart';
import { LubeUnit } from './LubeUnit/LubeUnit';
import { MotorCurrent } from './MotorCurrent/MotorCurrent';
import { RingBounce } from './RingBounce/RingBounce';
import { TagSelectionModal } from './TagSelectionModal';
import { SensorTag } from './customChartConfig';
import {
  ChartItemTab,
  ChartMode,
  CondensedHistoryItem,
  ProcessedTrendingData,
  TrendingRawItem,
  TrendingsChartData,
  TrendingsProps,
  TrendingStatistic,
} from './Trendings.types';
import {
  ChartTrendingsTabContainer,
  CustomHeaderContainer,
  LastUpdateTime,
  LineChartContainer,
  MotorCurrentTextContainer,
  ScrollableSelectorWrapper,
  StatisticsSelectorContainer,
  TabsTrendingsContainer,
  TrendingsDetailsContainer,
  TrendingsHeaderContainer,
  TrendingsLoadingContainer,
  TrendingsResponsiveRow,
  ViewModeContainer,
} from './Trendings.style';

const DEFAULT_STATISTIC: TrendingStatistic = TrendingStatistic.avg;
const MAX_HISTORY_DATA_LENGTH: number = 600;
const DATA_RANGE: number = 1000 * 60 * 10; // 10 minutes
const CONDENSED_TAGS: string = (
  'crusherMotor1Amps,crusherMotor2Amps,crusherMotorRPM,lubricationReturnTemperature,'
  + 'lubricationTankTemperature,lubricationSupplyTemperature,vibrationSensor1'
);

function Trendings({
  className,
  idEquipmentUnit,
  onCloseTrendings,
  operationalData,
  showTrendings = false,
  alternativeTrending,
  testId,
}: TrendingsProps): JSX.Element {
  // Dependencies
  const { t } = useTranslation();
  const theme: Theme = useTheme();
  const customer: Customer = getCustomer() as Customer;
  const user: User = getUser() as User;
  const {
    isXs,
    isSm,
    isMd,
    isPortrait,
  } = useBreakpoint();

  // Responsive conditions
  const isMobile: boolean = isXs || isSm;
  const isMobileOrSmallTablet: boolean = isMobile || isMd;
  const shouldShowRotateOverlay: boolean = isMobile && isPortrait;

  // Admin flag (used to bypass enable-flag filtering in TagSelectionModal)
  const isAdmin: boolean = user.type === UserType.admin;

  // Scroll fade refs and state for trending selector
  const scrollSelectorRef = useRef<HTMLDivElement>(null);

  // Ref to the Trendings container — used to anchor the TagSelectionModal overlay on desktop
  const trendingsContainerRef = useRef<HTMLDivElement>(null);

  /* **********************************************************************************************
  ******************************************** STATE **********************************************
  ********************************************************************************************** */
  type State = {
    autoRefreshInterval: NodeJS.Timeout | null;
    condensedData: CondensedHistoryItem[];
    dateRangeValue: DatePickerProps['value'];
    historyData: (EquipmentUnitDataVSICrusher | EdgeDeviceOperationalStatus)[];
    isAutoRefreshActive: boolean;
    isLoadingCondensedData: boolean;
    isLoadingHistoryData: boolean;
    liveAggregatedData: (EquipmentUnitDataVSICrusher | EdgeDeviceOperationalStatus)[];
    selectedCustomTags: SensorTag[];
    selectedStatistic: TrendingStatistic;
    selectedTrending: TrendingType;
    showLeftFade: boolean;
    showRightFade: boolean;
    showTagSelectionModal: boolean;
    startTimestamp: number | null | undefined;
    stopTimestamp: number | null | undefined;
  };

  const initialState: State = {
    autoRefreshInterval: null,
    condensedData: [],
    dateRangeValue: null,
    historyData: [],
    isAutoRefreshActive: false,
    isLoadingCondensedData: false,
    isLoadingHistoryData: false,
    liveAggregatedData: [],
    selectedCustomTags: [],
    selectedStatistic: DEFAULT_STATISTIC,
    selectedTrending: alternativeTrending || TrendingType.motorCurrent,
    showLeftFade: false,
    showRightFade: true,
    showTagSelectionModal: false,
    startTimestamp: null,
    stopTimestamp: null,
  };

  const [state, setState] = useState<State>(initialState);

  /* **********************************************************************************************
  ******************************************** METHODS ********************************************
  ********************************************************************************************** */

  /**
   * Build equipment data structure from edge device status when equipment data is unavailable
   */
  const buildEquipmentDataFromEdgeStatus = useCallback((
    edgeStatusArray: EdgeDeviceOperationalStatus[],
  ): (EquipmentUnitDataVSICrusher & { edgeStatus: string }
    )[] => edgeStatusArray.map((edgeItem) => ({
    timestamp: edgeItem.timestamp,
    edgeStatus: edgeItem.edgeStatus,
  } as EquipmentUnitDataVSICrusher & { edgeStatus: string })), []);

  /**
   * Apply edge status to historical data - uses single edge status for all data points
   * No 10s threshold applied (temporal differences are normal in historical data)
   */
  const applyEdgeStatusToHistoricalData = useCallback((
    data: CrusherOperationalData,
  ): (EquipmentUnitDataVSICrusher & { edgeStatus: string }
    )[] => {
    const [edgeStatuses, equipmentData] = _.partition(
      data,
      (item): item is EdgeDeviceOperationalStatus => 'edgeStatus' in item,
    );

    const processedEquipmentData = equipmentData as EquipmentUnitDataVSICrusher[];
    const noEdgeStatuses: boolean = edgeStatuses.length === 0;
    const noEquipmentData: boolean = processedEquipmentData.length === 0;
    const hasEdgeStatuses: boolean = edgeStatuses.length > 0;

    let result: (EquipmentUnitDataVSICrusher & { edgeStatus: string })[];

    if (noEdgeStatuses) {
      result = processedEquipmentData.map((item: EquipmentUnitDataVSICrusher): (
      EquipmentUnitDataVSICrusher & { edgeStatus: string }) => ({
        ...item,
        edgeStatus: EdgeDeviceOperationalStatusStatus.off,
      }));
    } else if (noEquipmentData && hasEdgeStatuses) {
      result = buildEquipmentDataFromEdgeStatus(edgeStatuses);
    } else {
      const singleEdgeStatus: EdgeDeviceOperationalStatusStatus = edgeStatuses[0]?.edgeStatus
        || EdgeDeviceOperationalStatusStatus.off;
      result = processedEquipmentData.map((item: EquipmentUnitDataVSICrusher): (
      EquipmentUnitDataVSICrusher & { edgeStatus: string }) => ({
        ...item,
        edgeStatus: singleEdgeStatus,
      }));
    }

    return result;
  }, [buildEquipmentDataFromEdgeStatus]);

  /**
   * Apply edge status to operational data - uses 10s threshold and temporal mapping
   * With 10s threshold to detect outdated data
   */
  const applyEdgeStatusToOperationalData = useCallback((
    data: CrusherOperationalData,
  ): (EquipmentUnitDataVSICrusher & { edgeStatus: string }
    )[] => {
    const [edgeStatuses, equipmentData] = _.partition(
      data,
      (item): item is EdgeDeviceOperationalStatus => 'edgeStatus' in item,
    );

    const processedEquipmentData = equipmentData as EquipmentUnitDataVSICrusher[];
    const noEdgeStatuses: boolean = edgeStatuses.length === 0;
    const noEquipmentData: boolean = processedEquipmentData.length === 0;
    const hasEdgeStatuses: boolean = edgeStatuses.length > 0;
    const hasEquipmentData: boolean = processedEquipmentData.length > 0;

    let result: (EquipmentUnitDataVSICrusher & { edgeStatus: string })[];

    if (noEdgeStatuses) {
      result = processedEquipmentData.map((item: EquipmentUnitDataVSICrusher): (
      EquipmentUnitDataVSICrusher & { edgeStatus: string }) => ({
        ...item,
        edgeStatus: EdgeDeviceOperationalStatusStatus.off,
      }));
    } else if (noEquipmentData && hasEdgeStatuses) {
      // Critical scenario 1: Only edge status available (equipment issues)
      result = buildEquipmentDataFromEdgeStatus(edgeStatuses);
    } else if (hasEdgeStatuses && hasEquipmentData) {
      const THRESHOLD_MS: number = 10 * 1000; // 10 seconds
      const latestEdgeTimestamp: number = Math.max(...edgeStatuses.map((edge) => edge.timestamp));
      const latestEquipmentTimestamp: number = Math.max(
        ...processedEquipmentData.map((equip) => equip.timestamp as number),
      );
      const timeDifference: number = Math.abs(latestEdgeTimestamp - latestEquipmentTimestamp);
      const isDataOutdated: boolean = timeDifference > THRESHOLD_MS;

      if (isDataOutdated) {
        // Critical scenario 2: Equipment data is outdated (threshold > 10s)
        result = buildEquipmentDataFromEdgeStatus(edgeStatuses);
      } else {
        // Normal scenario: Temporal mapping of operational data
        const sortedEquipment: EquipmentUnitDataVSICrusher[] = _.orderBy(
          processedEquipmentData,
          'timestamp',
          'asc',
        );
        const sortedEdgeStatuses: EdgeDeviceOperationalStatus[] = _.orderBy(
          edgeStatuses,
          'timestamp',
          'asc',
        );

        result = sortedEquipment.map((
          item,
        ): EquipmentUnitDataVSICrusher & { edgeStatus: string } => {
          const applicableEdgeStatus: EdgeDeviceOperationalStatus | undefined = sortedEdgeStatuses
            .filter((edge: EdgeDeviceOperationalStatus) => edge.timestamp <= (
              item.timestamp as number
            ))
            .pop();

          const edgeStatusValue:
          EdgeDeviceOperationalStatusStatus = applicableEdgeStatus?.edgeStatus
            || sortedEdgeStatuses[0]?.edgeStatus
            || 'off';

          return {
            ...item,
            edgeStatus: edgeStatusValue,
          };
        });
      }
    } else {
      result = processedEquipmentData.map((item: EquipmentUnitDataVSICrusher): (
      EquipmentUnitDataVSICrusher & { edgeStatus: string }) => ({
        ...item,
        edgeStatus: EdgeDeviceOperationalStatusStatus.off,
      }));
    }

    return result;
  }, [buildEquipmentDataFromEdgeStatus]);

  /**
   * Unify edge status application logic - delegates to appropriate function based on data type
   */
  const applyEdgeStatusToData = useCallback((
    data: CrusherOperationalData,
    isHistoricalData = false,
  ): (EquipmentUnitDataVSICrusher & { edgeStatus: string }
    )[] => (isHistoricalData
    ? applyEdgeStatusToHistoricalData(data)
    : applyEdgeStatusToOperationalData(data)
  ), [applyEdgeStatusToHistoricalData, applyEdgeStatusToOperationalData]);

  /**
   * Extract value from condensed data based on selected statistic
   * Handles both simple values and aggregated values (min/max/avg)
   */
  type CondensedValue = { max?: number; avg?: number; min?: number };
  const extractValueFromCondensedData = useCallback((
    value: number | CondensedValue | null | undefined,
    statistic: TrendingStatistic,
  ): number | null => extractValue(value, true, statistic), []);

  // Fetch history data
  const fetchHistoryData = useCallback(async (): Promise<void> => {
    setState((curr: State) => ({ ...curr, isLoadingHistoryData: true }));

    try {
      const response: CrusherOperationalData = await requestVantageApi
        .equipmentUnitDataMonitoring({
          path: `/history/crusher/${idEquipmentUnit}`,
          method: RequestMethod.GET,
          params: user.type === UserType.admin || user.type === UserType.service
            ? {
              idCustomer: customer.id,
              startTimestamp: Date.now() - DATA_RANGE,
              stopTimestamp: Date.now(),
            }
            : {
              startTimestamp: Date.now() - DATA_RANGE,
              stopTimestamp: Date.now(),
            },
        });

      const processedHistoryData: (
        EquipmentUnitDataVSICrusher & { edgeStatus: string }
      )[] = applyEdgeStatusToData(response, true);

      // wait for 250ms before setting the state
      await new Promise((resolve) => {
        setTimeout(resolve, 250);
      });

      setState((curr: State) => ({
        ...curr,
        historyData: processedHistoryData,
        liveAggregatedData: processedHistoryData,
        isLoadingHistoryData: false,
      }));
    } catch (error: unknown) {
      setState((curr: State) => ({
        ...curr,
        isLoadingHistoryData: false,
      }));
    }
  }, [
    applyEdgeStatusToData,
    customer,
    idEquipmentUnit,
    user.type,
  ]);

  // Fetch condensed data
  // customTags (optional) — when present (Custom tab with selected sensors),
  // overrides the fixed CONDENSED_TAGS list with a dynamic comma-separated string.
  const fetchCondensedData = useCallback(async (
    startTimestamp: number | null | undefined,
    stopTimestamp: number | null | undefined,
    customTags?: string,
  ): Promise<void> => {
    setState((curr: State) => ({ ...curr, isLoadingCondensedData: true }));

    const tagsParam: string = customTags || CONDENSED_TAGS;

    try {
      const response: CondensedHistoryItem[] = await requestVantageApi.equipmentUnitDataMonitoring({
        path: `/crusher/get_equipment_unit_chart_data/${idEquipmentUnit}`,
        method: RequestMethod.GET,
        handleErrors: false,
        params: user.type === UserType.admin || user.type === UserType.service
          ? {
            startTimestamp,
            stopTimestamp,
            maxPoints: MAX_HISTORY_DATA_LENGTH,
            idCustomer: customer.id,
            tags: tagsParam,
          }
          : {
            startTimestamp,
            stopTimestamp,
            maxPoints: MAX_HISTORY_DATA_LENGTH,
            tags: tagsParam,
          },
      });

      setState((curr: State) => ({
        ...curr,
        condensedData: response,
        isLoadingCondensedData: false,
      }));
    } catch (error: any) {
      if (
        error.response?.status === 422
        && error.response?.data === 'The selected time period must not exceed 90 days'
      ) {
        showToast({
          type: ToastType.error,
          text: t('view.crusher.trendings.maxPeriodError'),
        });
      } else {
        showToast({
          type: ToastType.error,
          text: t('common.defaultError'),
        });
      }

      setState((curr: State) => ({
        ...curr,
        isLoadingCondensedData: false,
        startTimestamp: null,
        stopTimestamp: null,
        dateRangeValue: null,
      }));
    }
  }, [
    idEquipmentUnit,
    customer,
    user,
    t,
  ]);

  /* **********************************************************************************************
  ************************************** COMPONENT HANDLING ***************************************
  ********************************************************************************************** */

  const chartData = useMemo<Array<TrendingsChartData>>(() => {
    const hasTimestampsAndData: boolean = !_.isNil(state.startTimestamp)
      && !_.isNil(state.stopTimestamp)
      && state.condensedData.length > 0;

    // HISTORY MODE: Use condensedData for selected period analysis
    // LIVE MODE: Use liveAggregatedData (that is kept dynamically)
    // - Starts with historyData (10min) on the first render
    // - Increments with operationalData (socket) and removes old data
    // - Always keeps the last 600 points or 10min
    const rawSourceData: TrendingRawItem[] = hasTimestampsAndData
      ? state.condensedData
      : state.liveAggregatedData as TrendingRawItem[];

    // Filter out invalid records BEFORE mapping
    const validSourceData: TrendingRawItem[] = rawSourceData.filter((raw: TrendingRawItem) => {
      // Must have valid timestamp
      if (!raw.timestamp || typeof raw.timestamp !== 'number') {
        return false;
      }
      return true;
    });

    return validSourceData.map((raw: TrendingRawItem) => {
      const rawData: ProcessedTrendingData = raw as ProcessedTrendingData;

      // For condensed data, extract the correct statistic value
      // For live data, use values directly
      const getValue = (
        value: number | CondensedValue | null | undefined,
      ): number | null => {
        if (value === undefined || value === null) return null;

        const extractedValue: number | null = hasTimestampsAndData
          ? extractValueFromCondensedData(value, state.selectedStatistic)
          : value as number | null;

        return extractedValue;
      };

      // Build result object with ONLY valid keys (non-null values)
      // Always include timestamp and edgeStatus (mandatory)
      const result: Partial<TrendingsChartData> = {
        timestamp: raw.timestamp as number,
        edgeStatus: convertEdgeStatus(rawData.edgeStatus),
      };

      // Only add keys with valid (non-null) values
      const crusherMotor1Amps: number | null = getValue(rawData.crusherMotor1Amps);
      if (crusherMotor1Amps !== null) {
        result.crusherMotor1Amps = crusherMotor1Amps;
      }

      const crusherMotor2Amps: number | null = getValue(rawData.crusherMotor2Amps);
      if (crusherMotor2Amps !== null) {
        result.crusherMotor2Amps = crusherMotor2Amps;
      }

      const crusherMotorRPM: number | null = getValue(rawData.crusherMotorRPM);
      if (crusherMotorRPM !== null) {
        result.crusherMotorRPM = crusherMotorRPM;
      }

      const lubricationSupplyTemperature:
      number | null = getValue(rawData.lubricationSupplyTemperature);
      if (lubricationSupplyTemperature !== null) {
        result.lubricationSupplyTemperature = lubricationSupplyTemperature;
      }

      const lubricationReturnTemperature:
      number | null = getValue(rawData.lubricationReturnTemperature);
      if (lubricationReturnTemperature !== null) {
        result.lubricationReturnTemperature = lubricationReturnTemperature;
      }

      const lubricationTankTemperature:
      number | null = getValue(rawData.lubricationTankTemperature);
      if (lubricationTankTemperature !== null) {
        result.lubricationTankTemperature = lubricationTankTemperature;
      }

      const vibrationSensor1: number | null = getValue(rawData.vibrationSensor1);
      if (vibrationSensor1 !== null) {
        result.vibrationSensor1 = vibrationSensor1;
      }

      return result as TrendingsChartData;
    });
  }, [
    state.startTimestamp,
    state.stopTimestamp,
    state.condensedData,
    state.liveAggregatedData,
    state.selectedStatistic,
    extractValueFromCondensedData,
  ]);

  // Extract the most recent operational data from history
  const lastVsiUnitData: EquipmentUnitDataVSICrusher | null = lastOperationalUnitDataVSI(
    operationalData,
  );

  // Determine chart mode based on data source and timestamps
  const chartMode: ChartMode = useMemo((): ChartMode => {
    const hasTimestamps: boolean = !_.isNil(state.startTimestamp) && !_.isNil(state.stopTimestamp);
    const hasCondensedData: boolean = state.condensedData.length > 0;
    const hasLiveAggregatedData: boolean = state.liveAggregatedData.length > 0;

    // HISTORY MODE: When user selects a date range that does NOT include today
    if (hasTimestamps && hasCondensedData) {
      const today: Date = new Date();
      const stopDate: Date = new Date(state.stopTimestamp as number);
      const isToday: boolean = (
        stopDate.getFullYear() === today.getFullYear()
        && stopDate.getMonth() === today.getMonth()
        && stopDate.getDate() === today.getDate()
      );

      // If the selected date range includes today, it's live-aggregate mode
      if (isToday) {
        return ChartMode.liveAggregate;
      }

      // If the selected date range does NOT include today, it's history mode
      return ChartMode.history;
    }

    // LIVE MODE: When no timestamps are selected and we have live aggregated data (10min)
    if (!hasTimestamps && hasLiveAggregatedData) {
      return ChartMode.live;
    }

    return ChartMode.history;
  }, [
    state.startTimestamp,
    state.stopTimestamp,
    state.condensedData.length,
    state.liveAggregatedData.length,
  ]);

  const hasTimestampsAndData: boolean = useMemo((): boolean => (
    !_.isNil(state.startTimestamp)
    && !_.isNil(state.stopTimestamp)
    && state.condensedData.length > 0
  ), [state.startTimestamp, state.stopTimestamp, state.condensedData.length]);

  const renderChartComponent = useCallback((): JSX.Element | null => {
    const isDataLoaded: boolean = !state.isLoadingHistoryData;
    const hasDateRange: boolean = !_.isNil(state.startTimestamp) && !_.isNil(state.stopTimestamp);
    const hasNoCondensedData: boolean = state.condensedData.length === 0;
    const isCustomTab: boolean = state.selectedTrending === TrendingType.custom;
    // Custom tab handles its own empty state via CustomChart component
    const shouldShowEmptyState: boolean = hasDateRange && hasNoCondensedData && !isCustomTab;

    let chartElement: JSX.Element | null = null;

    if (shouldShowEmptyState) {
      chartElement = (
        <EmptyBox
          title={t('view.crusher.trendings.emptyBox.title')}
          description={t('view.crusher.trendings.emptyBox.description')}
          icon={<CloudOffLine />}
        />
      );
    } else if (isDataLoaded) {
      const rawData: TrendingRawItem[] = hasTimestampsAndData
        ? state.condensedData as TrendingRawItem[]
        : state.liveAggregatedData as TrendingRawItem[];

      const chartComponents: Partial<Record<TrendingType, JSX.Element>> = {
        motorCurrent: (
          <MotorCurrent
            testId="motor-current"
            trendingMotorCurrentData={chartData}
            chartMode={chartMode}
            vsiUnitData={lastVsiUnitData}
          />
        ),
        lubeUnit: (
          <LubeUnit
            testId="lube-unit"
            trendingLubeUnitData={chartData}
            chartMode={chartMode}
            operationalData={operationalData}
          />
        ),
        ringBounceSensor: (
          <RingBounce
            testId="ring-bounce"
            trendingRingBounceSensorData={chartData}
            chartMode={chartMode}
            unitData={lastVsiUnitData}
          />
        ),
        custom: (
          <CustomChart
            chartMode={chartMode}
            hasCondensedData={hasTimestampsAndData}
            onOpenTagSelection={() => setState((curr: State) => ({
              ...curr,
              showTagSelectionModal: true,
            }))}
            onRemoveTag={(tag: SensorTag) => setState((curr: State) => ({
              ...curr,
              selectedCustomTags: curr.selectedCustomTags.filter(
                (selectedTag: SensorTag) => selectedTag !== tag,
              ),
            }))}
            rawData={rawData}
            selectedStatistic={state.selectedStatistic}
            selectedTags={state.selectedCustomTags}
            testId="custom-chart"
          />
        ),
      };
      chartElement = chartComponents[state.selectedTrending] ?? null;
    }

    return chartElement;
  }, [
    chartData,
    chartMode,
    hasTimestampsAndData,
    lastVsiUnitData,
    operationalData,
    state.condensedData,
    state.isLoadingHistoryData,
    state.liveAggregatedData,
    state.selectedCustomTags,
    state.selectedStatistic,
    state.selectedTrending,
    state.startTimestamp,
    state.stopTimestamp,
    t,
  ]);

  const DEFAULT_TRANSLATE_PATH: string = 'view.crusher.trendings';
  const VSI_TRANSLATE_PATH: string = 'view.crusher.vsi.trendings';

  const itemsToTrendingsTab: ChartItemTab[] = useMemo((): ChartItemTab[] => {
    const vsiTabs: ChartItemTab[] = [
      {
        key: TrendingType.motorCurrent,
        label: t(`${DEFAULT_TRANSLATE_PATH}.motorCurrent.title`),
      },
      {
        key: TrendingType.lubeUnit,
        label: t(`${DEFAULT_TRANSLATE_PATH}.lubeUnit.title`),
      },
    ];
    if (lastVsiUnitData?.vibrationSensor1Enable) {
      vsiTabs.push({
        key: TrendingType.ringBounceSensor,
        label: t(`${VSI_TRANSLATE_PATH}.ringBounceSensor.title`),
      });
    }
    vsiTabs.push({
      key: TrendingType.custom,
      label: t(`${DEFAULT_TRANSLATE_PATH}.custom.title`),
    });
    return vsiTabs;
  }, [t, lastVsiUnitData?.vibrationSensor1Enable]);

  // OptionsSwitch format for mobile trend selector
  const trendingOptions: { label: string; value: string }[] = useMemo(
    () => itemsToTrendingsTab.map((item: ChartItemTab) => ({
      label: item.label,
      value: item.key,
    })),
    [itemsToTrendingsTab],
  );

  const handleChangeChartRange = useCallback((info: MenuItemType): void => {
    const clickedKey = String((info)?.key);
    if (clickedKey === String(CrusherDashboardChartRange.live)) {
      setState((curr: State) => {
        // clear auto-refresh
        if (curr.autoRefreshInterval) {
          clearInterval(curr.autoRefreshInterval);
        }

        return {
          ...curr,
          startTimestamp: null,
          stopTimestamp: null,
          condensedData: [],
          dateRangeValue: null,
          autoRefreshInterval: null,
          isAutoRefreshActive: false,
        };
      });
    }
  }, []);

  const SEGMENTED_OPTIONS: { label: string; value: TrendingStatistic }[] = [
    { label: t('common.min'), value: TrendingStatistic.min },
    { label: t('common.average'), value: TrendingStatistic.avg },
    { label: t('common.max'), value: TrendingStatistic.max },
  ];

  const handleChangeTrending = useCallback((info: MenuItemType): void => {
    const selectedTrending: TrendingType = info.key as TrendingType;

    setState((curr: State) => ({
      ...curr,
      selectedTrending,
    }));
  }, []);

  const selectedTabKey: CrusherDashboardChartRange | undefined = useMemo(() => {
    if (chartMode === ChartMode.live || chartMode === ChartMode.liveAggregate) {
      return CrusherDashboardChartRange.live;
    }
    return undefined;
  }, [chartMode]);

  const updateSelectorFadeVisibility = useCallback((): void => {
    const container: HTMLDivElement | null = scrollSelectorRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    const isScrollable: boolean = scrollWidth > clientWidth;
    const isAtStart: boolean = scrollLeft <= 1;
    const isAtEnd: boolean = scrollLeft + clientWidth >= scrollWidth - 1;

    setState((curr: State) => ({
      ...curr,
      showLeftFade: isScrollable && !isAtStart,
      showRightFade: isScrollable && !isAtEnd,
    }));
  }, []);

  useEffect(() => {
    const container: HTMLDivElement | null = scrollSelectorRef.current;
    if (!container) return undefined;

    updateSelectorFadeVisibility();

    const resizeObserver: ResizeObserver = new ResizeObserver(updateSelectorFadeVisibility);
    resizeObserver.observe(container);

    container.addEventListener('scroll', updateSelectorFadeVisibility);

    return (): void => {
      resizeObserver.disconnect();
      container.removeEventListener('scroll', updateSelectorFadeVisibility);
    };
  }, [updateSelectorFadeVisibility]);

  // Fetch historical data ONLY on the first render (independent of mode)
  // - Ensure that liveAggregatedData always has base data
  // - Necessary to avoid gaps when returning to live mode
  useEffect((): void => {
    if (
      showTrendings
      && state.historyData.length === 0
      && !state.isLoadingHistoryData
    ) {
      fetchHistoryData();
    }
  }, [
    showTrendings,
    state.historyData.length,
    state.isLoadingHistoryData,
    fetchHistoryData,
  ]);

  // ALWAYS increment liveAggregatedData with socket data (independent of mode)
  // - Ensure that there are no gaps when returning from historical mode to live mode
  // - Keep the last 600 data points updated
  useEffect((): void => {
    if (operationalData.length > 0) {
      setState((curr: State) => {
        const mergedOperationalData: (
          EquipmentUnitDataVSICrusher & { edgeStatus: string }
        )[] = applyEdgeStatusToData(operationalData, false);
        const updatedData: (
          EquipmentUnitDataVSICrusher | EdgeDeviceOperationalStatus
        )[] = [...curr.liveAggregatedData, ...mergedOperationalData];

        // Keep only the last 600 points (or last 10min)
        const limitedData: (
          EquipmentUnitDataVSICrusher | EdgeDeviceOperationalStatus
        )[] = updatedData.slice(-MAX_HISTORY_DATA_LENGTH);

        return {
          ...curr,
          liveAggregatedData: limitedData,
        };
      });
    }
  }, [operationalData, applyEdgeStatusToData]);

  // Reload data when dateRange changes (but not during auto-refresh)
  useEffect((): void => {
    const shouldFetchCondensed: boolean = (
      !_.isNil(state.startTimestamp) && !_.isNil(state.stopTimestamp) && !state.isAutoRefreshActive
    );
    if (shouldFetchCondensed) {
      const customTags: string | undefined = state.selectedTrending === TrendingType.custom
        && state.selectedCustomTags.length > 0
        ? state.selectedCustomTags.join(',')
        : undefined;
      fetchCondensedData(state.startTimestamp, state.stopTimestamp, customTags);
    }
  }, [
    state.startTimestamp,
    state.stopTimestamp,
    state.isAutoRefreshActive,
    state.selectedTrending,
    state.selectedCustomTags,
    fetchCondensedData,
  ]);

  // Configure auto-refresh when timestamps change
  useEffect((): (() => void) => {
    const shouldFetchCondensed: boolean = (
      !_.isNil(state.startTimestamp) && !_.isNil(state.stopTimestamp)
    );

    // Always clear previous interval to avoid multiple intervals
    setState((curr: State) => {
      if (curr.autoRefreshInterval) {
        clearInterval(curr.autoRefreshInterval);
      }
      return {
        ...curr,
        autoRefreshInterval: null,
        isAutoRefreshActive: false,
      };
    });

    let timer: NodeJS.Timeout | null = null;
    let intervalId: NodeJS.Timeout | null = null;

    if (shouldFetchCondensed) {
      // Capture current values to avoid obsolete closures
      const currentStartTimestamp: number | null | undefined = state.startTimestamp;
      const currentStopTimestamp: number | null | undefined = state.stopTimestamp;

      // Verify if auto-refresh should be configured
      const shouldAutoRefresh: boolean = (() => {
        if (!_.isNil(currentStartTimestamp) && !_.isNil(currentStopTimestamp)) {
          const today: Date = new Date();
          today.setHours(0, 0, 0, 0); // Start of the current day
          const todayTimestamp: number = today.getTime();

          const endOfToday: Date = new Date();
          endOfToday.setHours(23, 59, 59, 999); // End of the current day
          const endOfTodayTimestamp: number = endOfToday.getTime();

          // Verify if the range includes any part of the current day
          return (
            (currentStartTimestamp <= endOfTodayTimestamp && currentStopTimestamp >= todayTimestamp)
          );
        }
        return false;
      })();

      if (shouldAutoRefresh) {
        // Mark that auto-refresh is active
        setState((curr: State) => ({
          ...curr,
          isAutoRefreshActive: true,
        }));

        // Dynamic tags for Custom tab auto-refresh.
        // These values are captured once when the effect runs. If selectedCustomTags changes,
        // the effect re-executes (deps include them), tearing down and recreating the timer.
        const autoRefreshCustomTags: string | undefined = (
          state.selectedTrending === TrendingType.custom
          && state.selectedCustomTags.length > 0
        )
          ? state.selectedCustomTags.join(',')
          : undefined;

        // Fetch data immediately when range changes
        fetchCondensedData(currentStartTimestamp, currentStopTimestamp, autoRefreshCustomTags);

        // Configure auto-refresh after fetching initial data
        const autoRefreshTagsParam: string = autoRefreshCustomTags || CONDENSED_TAGS;
        timer = setTimeout(() => {
          intervalId = setInterval(async () => {
            try {
              // A every minute, fetch data up to the current time
              const nowTimestamp: number = Date.now();

              const response: CondensedHistoryItem[] = await requestVantageApi
                .equipmentUnitDataMonitoring({
                  path: `/crusher/get_equipment_unit_chart_data/${idEquipmentUnit}`,
                  method: RequestMethod.GET,
                  params: user.type === UserType.admin || user.type === UserType.service
                    ? {
                      startTimestamp: currentStartTimestamp,
                      stopTimestamp: nowTimestamp, // Always up to the current time
                      maxPoints: MAX_HISTORY_DATA_LENGTH, // LIMIT OF MAX_HISTORY_DATA_LENGTH POINTS
                      idCustomer: customer.id,
                      tags: autoRefreshTagsParam,
                    }
                    : {
                      startTimestamp: currentStartTimestamp,
                      stopTimestamp: nowTimestamp, // Always up to the current time
                      maxPoints: MAX_HISTORY_DATA_LENGTH, // LIMIT OF MAX_HISTORY_DATA_LENGTH POINTS
                      tags: autoRefreshTagsParam,
                    },
                });

              setState((curr: State) => ({
                ...curr,
                condensedData: response,
              }));
            } catch (error: unknown) {
              setState((curr: State) => ({
                ...curr,
                condensedData: [],
              }));
            }
          }, 60000); // 1 minute = 60000ms

          setState((curr: State) => ({
            ...curr,
            autoRefreshInterval: intervalId,
          }));
        }, 1000);
      } else {
        // If auto-refresh should not be configured, clear the flag
        setState((curr: State) => ({
          ...curr,
          isAutoRefreshActive: false,
        }));
      }
    }

    return (): void => {
      if (timer) clearTimeout(timer);
      if (intervalId) clearInterval(intervalId);
    };
  }, [
    customer,
    idEquipmentUnit,
    state.selectedCustomTags,
    state.selectedTrending,
    state.startTimestamp,
    state.stopTimestamp,
    user,
    fetchCondensedData,
  ]);

  // Clear auto-refresh when modal is closed
  useEffect((): void => {
    if (!showTrendings) {
      setState((curr: State) => {
        if (curr.autoRefreshInterval) {
          clearInterval(curr.autoRefreshInterval);
        }
        return {
          ...curr,
          autoRefreshInterval: null,
          isAutoRefreshActive: false,
        };
      });
    }
  }, [showTrendings]);

  // Custom header for mobile/tablet: trend selector + statistics selector (replaces modal title)
  const mobileCustomHeader: React.ReactNode = isMobileOrSmallTablet ? (
    <CustomHeaderContainer data-testid="mobile-custom-header">
      <ScrollableSelectorWrapper
        ref={scrollSelectorRef}
        showLeftFade={state.showLeftFade}
        showRightFade={state.showRightFade}
        onScroll={updateSelectorFadeVisibility}
      >
        <OptionsSwitch
          value={state.selectedTrending}
          options={trendingOptions}
          onChange={
            (value: OptionsSwitchProps['value']) => setState((curr: State) => ({
              ...curr,
              selectedTrending: value as TrendingType,
            }))
          }
          testId="trending-selector-mobile"
        />
      </ScrollableSelectorWrapper>
      {
        !_.isNil(state.startTimestamp)
        && !_.isNil(state.stopTimestamp)
        && state.condensedData.length > 0
        && (
          <StatisticsSelectorContainer>
            <OptionsSwitch
              value={state.selectedStatistic}
              options={SEGMENTED_OPTIONS}
              onChange={
                (value: OptionsSwitchProps['value']) => setState((curr: State) => ({
                  ...curr,
                  selectedStatistic: value as TrendingStatistic,
                }))
              }
              testId="statistic-selector-mobile"
            />
          </StatisticsSelectorContainer>
        )
      }
    </CustomHeaderContainer>
  ) : undefined;

  return (
    <ModalPanel
      testId={testId}
      className={className}
      onClose={onCloseTrendings}
      showModal={showTrendings}
      title={
        isMobileOrSmallTablet
          ? undefined
          : t('view.crusher.trendings.title') || undefined
      }
      customHeader={
        shouldShowRotateOverlay
          ? undefined
          : mobileCustomHeader
      }
      size={ModalSize.xl}
    >
      {shouldShowRotateOverlay ? (
        <RotateDeviceOverlay testId="rotate-device-overlay" />
      ) : (
        <TrendingsHeaderContainer
          data-testid="trendings-container"
          ref={trendingsContainerRef}
        >
          {isMobileOrSmallTablet ? (
            <>
              {/* Row: Live + DatePicker + LastUpdate */}
              <TrendingsResponsiveRow data-testid="selectors-row">
                <ViewModeContainer>
                  <NavigationTabs
                    items={[
                      {
                        key: CrusherDashboardChartRange.live,
                        label: t('common.live'),
                      },
                    ]}
                    onClick={handleChangeChartRange}
                    selected={selectedTabKey}
                  />
                  <DatePicker
                    testId="date-picker-mobile"
                    onChange={(value: DatePickerProps['value']) => setState((curr: State) => ({
                      ...curr,
                      dateRangeValue: value,
                      startTimestamp: Array.isArray(value) ? value[0]?.valueOf() : null,
                      stopTimestamp: Array.isArray(value) ? value[1]?.valueOf() : null,
                    }))}
                    value={state.dateRangeValue}
                  />
                  {/* Custom tab with sensors: button lives here on mobile to save chart space */}
                  {state.selectedTrending === TrendingType.custom
                    && state.selectedCustomTags.length > 0 && (
                    <Button
                      block={false}
                      onClick={() => setState((curr: State) => ({
                        ...curr,
                        showTagSelectionModal: true,
                      }))}
                      size={ButtonSize.xs}
                      testId="configure-sensors-header-button"
                      type={ButtonType.link}
                    >
                      <Plus />
                      {t('view.crusher.trendings.custom.configureSensors')}
                    </Button>
                  )}
                </ViewModeContainer>
                <Paragraph
                  size={ParagraphSize.sm}
                  color={theme.color.crusherDashboard.trendings.lastUpdateTextColor}
                  testId="last-update-item"
                >
                  <Trans
                    i18nKey="common.lastUpdate"
                    values={{ lastUpdate: timestampToTimeAgo(lastVsiUnitData?.timestamp) }}
                    components={{ lastUpdateTime: <LastUpdateTime /> }}
                  />
                </Paragraph>
              </TrendingsResponsiveRow>
            </>
          ) : (
            <>
              {/* Desktop layout (unchanged) */}
              <TabsTrendingsContainer>
                <NavigationTabs
                  items={itemsToTrendingsTab}
                  onClick={handleChangeTrending}
                  selected={state.selectedTrending}
                />
                <Paragraph
                  size={ParagraphSize.sm}
                  color={theme.color.crusherDashboard.trendings.lastUpdateTextColor}
                  testId="last-update-item"
                >
                  <Trans
                    i18nKey="common.lastUpdate"
                    values={{ lastUpdate: timestampToTimeAgo(lastVsiUnitData?.timestamp) }}
                    components={{ lastUpdateTime: <LastUpdateTime /> }}
                  />
                </Paragraph>
              </TabsTrendingsContainer>
              <TrendingsDetailsContainer>
                <MotorCurrentTextContainer>
                  <ViewModeContainer>
                    <NavigationTabs
                      items={[
                        {
                          key: CrusherDashboardChartRange.live,
                          label: t('common.live'),
                        },
                      ]}
                      onClick={handleChangeChartRange}
                      selected={selectedTabKey}
                    />
                    <DatePicker
                      onChange={(
                        value: DatePickerProps['value'],
                      ) => setState((curr: State) => ({
                        ...curr,
                        dateRangeValue: value,
                        startTimestamp: Array.isArray(value) ? value[0]?.valueOf() : null,
                        stopTimestamp: Array.isArray(value) ? value[1]?.valueOf() : null,
                      }))}
                      value={state.dateRangeValue}
                    />
                  </ViewModeContainer>
                </MotorCurrentTextContainer>
                <ChartTrendingsTabContainer>
                  {
                    !_.isNil(state.startTimestamp)
                    && !_.isNil(state.stopTimestamp)
                    && state.condensedData.length > 0
                    && (
                      <OptionsSwitch
                        value={state.selectedStatistic}
                        options={SEGMENTED_OPTIONS}
                        onChange={
                          (value: OptionsSwitchProps['value']) => setState((curr: State) => ({
                            ...curr,
                            selectedStatistic: value as TrendingStatistic,
                          }))
                        }
                      />
                    )
                  }
                </ChartTrendingsTabContainer>
              </TrendingsDetailsContainer>
            </>
          )}
          <LineChartContainer data-testid="chart-container">
            {
              (state.isLoadingCondensedData || state.isLoadingHistoryData) ? (
                <TrendingsLoadingContainer data-testid="loading-container">
                  <Loader
                    size={LoaderSize.xl}
                    text={`${t('common.loader')}`}
                    testId="loader-with-text"
                  />
                </TrendingsLoadingContainer>
              ) : renderChartComponent()
            }
          </LineChartContainer>

          <TagSelectionModal
            getContainer={
              isMobileOrSmallTablet
                ? undefined
                : () => trendingsContainerRef.current || document.body
            }
            isAdmin={isAdmin}
            lastUnitData={lastVsiUnitData}
            onApply={(tags: SensorTag[]) => {
              setState((curr: State) => ({
                ...curr,
                selectedCustomTags: tags,
                showTagSelectionModal: false,
              }));
            }}
            onCancel={() => setState((curr: State) => ({
              ...curr,
              showTagSelectionModal: false,
            }))}
            selectedTags={state.selectedCustomTags}
            showModal={state.showTagSelectionModal}
            testId="tag-selection-modal"
          />
        </TrendingsHeaderContainer>
      )}
    </ModalPanel>
  );
}

export { Trendings };
